import React, { useState, useRef, useEffect, useCallback } from "react";
import { FileText, Download, Upload, AlertCircle, Image as ImageIcon, Loader2, Eye, Pencil, Trash2, History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { downloadCSV } from "@/lib/csvDownload";
import { parseProductCSV, readFileAsText } from "@/lib/productCsvParser";
import type { ValidationError, ParsedProduct, UploadResult } from "@/lib/productCsvParser";

// ─── PDF types ────────────────────────────────────────────────────────────────

interface PDFParsedProduct {
  title: string;
  description: string;
  brand_owner: string;
  brand_name: string;
  subbrand_name?: string;
  ingredients: string;
  serving_size: number;
  serving_size_unit: string;
  household_serving_fulltext: string;
  branded_food_category?: string;
  package_weight?: string;
  market_country?: string;
  data_source?: string;
  has_product_image?: boolean;
  extracted_image_url?: string | null;
  nutrients: { nutrient_id: number; nutrient_name: string; amount: number; unit: string; matched: boolean }[];
}

type UploadMethod = "csv" | "pdf" | "image";

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

async function pdfToPageImages(file: File): Promise<string[]> {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const images: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.5 }); // Good balance of quality vs size
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport }).promise;
    images.push(canvas.toDataURL('image/jpeg', 0.8)); // JPEG for smaller payload
    canvas.remove();
  }

  return images;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ProductUploadContentProps {
  isAdmin: boolean;
}

export default function ProductUploadContent({ isAdmin }: ProductUploadContentProps) {
  const [method, setMethod] = useState<UploadMethod>("csv");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // PDF-specific state
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfProducts, setPdfProducts] = useState<PDFParsedProduct[]>([]);
  const [pdfReviewMode, setPdfReviewMode] = useState(false);
  const [pdfImageFile, setPdfImageFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);

  // View uploads state
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [previousUploads, setPreviousUploads] = useState<{ fdc_id: number; title: string; description: string | null; serving_size: number; serving_size_unit: string; created_at: string; image: string | null }[]>([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [exportingTable, setExportingTable] = useState<string | null>(null);

  const handleExportTable = async (tableName: 'branded_food' | 'food_images' | 'food_nutrients') => {
    setExportingTable(tableName);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Not authenticated'); return; }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/product-upload?export=${tableName}`,
        { headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      const rows = json.rows as Record<string, unknown>[];

      if (!rows || rows.length === 0) { toast.info('No data to export'); return; }

      const headers = Object.keys(rows[0]);
      const csvData = rows.map(r => headers.map(h => r[h] ?? ''));
      downloadCSV({ headers, data: csvData as (string | number)[][], filename: `${tableName}_export_${new Date().toISOString().slice(0, 10)}.csv` });
      toast.success(`Exported ${rows.length} rows from ${tableName}`);
    } catch (err: any) {
      toast.error(`Export failed: ${err.message}`);
    } finally {
      setExportingTable(null);
    }
  };

  const fetchPreviousUploads = useCallback(async () => {
    setLoadingUploads(true);
    try {
      const { data, error } = await supabase.functions.invoke('product-upload', {
        method: 'GET',
      });

      if (error) throw error;

      setPreviousUploads(data?.uploads || []);
    } catch (err: any) {
      toast.error('Failed to load previous uploads');
      console.error(err);
    } finally {
      setLoadingUploads(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "view") {
      fetchPreviousUploads();
    }
  }, [activeTab, fetchPreviousUploads]);

  if (!isAdmin) return null;

  // ─── CSV handlers ─────────────────────────────────────────────────────────

  const handleDownloadTemplate = () => {
    downloadCSV({
      filename: 'product_upload_template.csv',
      headers: [
        'group', 'title', 'description', 'brand_owner', 'brand_name', 'subbrand_name',
        'ingredients', 'serving_size', 'serving_size_unit', 'household_serving_fulltext',
        'branded_food_category', 'data_source', 'gtin_upc', 'package_weight', 'market_country',
        'image', 'nutrient_id', 'nutrient_title', 'amount',
      ],
      data: [
        ['1', 'Example Product', 'A great product description', 'Acme Corp', 'Acme', '', 'Water, Sugar, Natural Flavors', '28', 'g', '1 oz (28g)', 'Snacks', 'LI', '', '', 'United States', 'https://example.com/product-image.jpg', '1003', 'Protein', '5'],
        ['1', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '1004', 'Total Fat', '10'],
        ['2', 'Second Product', 'Another product', 'Brant Foods', 'Brant', '', 'Oats, Honey', '100', 'g', '100g', 'Breakfast', 'LI', '012345678901', '400g', 'United States', 'https://example.com/product2.jpg', '1003', 'Protein', '8'],
      ],
    });
  };

  const handleCSVUpload = async () => {
    if (!file) { toast.error('Please select a CSV file first'); return; }
    setErrors([]); setResults([]); setUploadComplete(false); setIsUploading(true);

    try {
      const text = await readFileAsText(file);
      const { products, errors: parseErrors } = parseProductCSV(text);

      if (parseErrors.length > 0) { setErrors(parseErrors); setIsUploading(false); return; }
      if (products.length === 0) { toast.error('No valid products found'); setIsUploading(false); return; }

      const { data, error } = await supabase.functions.invoke('product-upload', { body: { products } });
      if (error) throw new Error(error.message || 'Upload failed');
      if (!data?.results) throw new Error('No results returned');

      setResults(data.results);
      setUploadComplete(true);
      toast.success(`${data.results.length} product(s) uploaded successfully`);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // ─── PDF / Image parse handler ─────────────────────────────────────────

  const handleDocumentParse = async () => {
    if (!file && !imageFiles.length) { toast.error('Please select a file first'); return; }
    setPdfParsing(true); setPdfProducts([]); setPdfReviewMode(false); setErrors([]);

    try {
      let pageImages: string[];

      if (method === "image") {
        pageImages = await Promise.all(imageFiles.map(fileToBase64));
        toast.info(`${pageImages.length} image(s) ready. Scanning for products...`);
      }

      if (method === "pdf") {
        // ── PDF: Convert pages to images first ──
        toast.info('Converting PDF pages to images...');
        pageImages = await pdfToPageImages(file!);
        toast.info(`Converted ${pageImages.length} page(s). Scanning for products...`);
      }

      // Phase 1: Scan — send all pages to AI to identify products & page ranges
      const { data: scanData, error: scanError } = await supabase.functions.invoke('product-pdf-parse', {
        body: { pageImages, mode: 'scan' },
      });

      if (scanError) throw new Error(scanError.message || 'Scan failed');
      const productMap = scanData?.products as { title: string; start_page: number; end_page: number; has_product_image: boolean; image_page: number | null }[];
      if (!productMap?.length) { toast.error('No products identified in PDF'); return; }

      toast.success(`Identified ${productMap.length} product(s). Extracting details...`);

      // Phase 2: Extract — process each product's pages individually
      const allProducts: PDFParsedProduct[] = [];
      const DELAY_MS = 1500; // delay between requests to avoid rate limits

      for (let i = 0; i < productMap.length; i++) {
        const pm = productMap[i];
        const productPages = pageImages.slice(pm.start_page - 1, pm.end_page);
        // Calculate image_page relative to the batch (1-indexed within the batch)
        const relativeImagePage = pm.image_page ? pm.image_page - pm.start_page + 1 : null;
        toast.info(`Extracting product ${i + 1}/${productMap.length}: ${pm.title}...`);

        const { data, error } = await supabase.functions.invoke('product-pdf-parse', {
          body: {
            pageImages: productPages,
            mode: 'extract',
            image_page: relativeImagePage,
            has_product_image: pm.has_product_image,
          },
        });

        if (error) {
          console.error(`Failed to extract product "${pm.title}":`, error);
          toast.error(`Failed to extract: ${pm.title}`);
          continue; // skip this product, continue with others
        }

        if (data?.products?.length) {
          allProducts.push(...data.products);
        }

        // Wait between requests to avoid rate limits
        if (i < productMap.length - 1) {
          await new Promise(resolve => setTimeout(resolve, DELAY_MS));
        }
      }

      if (allProducts.length === 0) { toast.error('No products extracted'); return; }

      setPdfProducts(allProducts);
      setPdfReviewMode(true);
      toast.success(`Extracted ${allProducts.length} product(s) successfully`);
    } catch (err: any) {
      toast.error(err.message || 'Parsing failed');
    } finally {
      setPdfParsing(false);
    }
  };

  const handlePDFConfirmUpload = async () => {
    if (pdfProducts.length === 0) return;
    setIsUploading(true);

    try {
      // Upload manually attached image to Supabase Storage if provided
      let manualImageUrl: string | null = null;
      if (pdfImageFile) {
        const ext = pdfImageFile.name.split('.').pop() || 'jpg';
        const path = `uploads/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(path, pdfImageFile);

        if (uploadError) {
          console.error('Image upload error:', uploadError);
          toast.error('Failed to upload product image');
        } else {
          const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(path);
          manualImageUrl = urlData.publicUrl;
        }
      }

      // Convert PDF products to upload format
      const products: ParsedProduct[] = pdfProducts.map((p) => {
        // Use extracted image from PDF, or manual image, or empty
        const imageUrl = p.extracted_image_url || manualImageUrl;
        return {
          title: p.title,
          description: p.description || '',
          brand_owner: p.brand_owner || '',
          brand_name: p.brand_name || '',
          subbrand_name: p.subbrand_name,
          ingredients: p.ingredients || '',
          serving_size: p.serving_size,
          serving_size_unit: p.serving_size_unit,
          household_serving_fulltext: p.household_serving_fulltext,
          branded_food_category: p.branded_food_category,
          data_source: p.data_source || 'LI',
          gtin_upc: undefined,
          package_weight: p.package_weight,
          market_country: p.market_country || 'United States',
          images: imageUrl ? [{ image: imageUrl }] : [],
          nutrients: p.nutrients
            .filter((n) => n.matched && n.nutrient_id > 0)
            .map((n) => ({ nutrient_id: n.nutrient_id, amount: n.amount, nutrient_title: n.nutrient_name })),
        };
      });

      const { data, error } = await supabase.functions.invoke('product-upload', { body: { products } });
      if (error) throw new Error(error.message || 'Upload failed');

      setResults(data.results);
      setUploadComplete(true);
      setPdfReviewMode(false);
      setPdfProducts([]);
      toast.success(`${data.results.length} product(s) uploaded successfully`);
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePdfProduct = (index: number) => {
    setPdfProducts((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Common handlers ──────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setErrors([]); setResults([]); setUploadComplete(false);
    setPdfProducts([]); setPdfReviewMode(false); setImageFiles([]);
  };

  const handleImageFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setImageFiles(files);
    setErrors([]); setResults([]); setUploadComplete(false);
    setPdfProducts([]); setPdfReviewMode(false);
  };

  const handleMethodChange = (m: UploadMethod) => {
    setMethod(m);
    setFile(null); setErrors([]); setResults([]); setUploadComplete(false);
    setPdfProducts([]); setPdfReviewMode(false); setPdfImageFile(null); setImageFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Product Upload</h1>
        <p className="text-muted-foreground">Upload products to the staging catalog. Changes are reviewed before going live.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upload" className="gap-2"><Upload className="h-4 w-4" /> Upload</TabsTrigger>
          <TabsTrigger value="view" className="gap-2"><History className="h-4 w-4" /> View Uploads</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6 mt-4">
      <Card>
        <CardHeader>
          <CardTitle>Upload Method</CardTitle>
          <CardDescription>Select how you want to upload product data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Method selector */}
          <div className="flex flex-wrap gap-3">
            <Button variant={method === "csv" ? "default" : "outline"} className="gap-2" onClick={() => handleMethodChange("csv")}>
              <FileText className="h-4 w-4" /> CSV
            </Button>
            <Button variant={method === "pdf" ? "default" : "outline"} className="gap-2" onClick={() => handleMethodChange("pdf")}>
              <FileText className="h-4 w-4" /> PDF
            </Button>
            <Button variant={method === "image" ? "default" : "outline"} className="gap-2" onClick={() => handleMethodChange("image")}>
              <ImageIcon className="h-4 w-4" /> Image
            </Button>
          </div>

          <Separator />

          {/* CSV upload section */}
          {method === "csv" && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Download the template, fill in your product data, then upload the completed file.
                  Use the <span className="font-medium">group</span> column to group multiple nutrient or image rows for the same product.
                </p>
                <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                  <Download className="h-4 w-4" /> Download CSV Template
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="csv-file">Select CSV File</Label>
                <Input id="csv-file" type="file" accept=".csv" ref={fileInputRef} onChange={handleFileChange} className="cursor-pointer" />
                {file && <p className="text-sm text-muted-foreground">Selected: <span className="font-medium">{file.name}</span></p>}
              </div>
              <Button onClick={handleCSVUpload} disabled={!file || isUploading} className="gap-2">
                <Upload className="h-4 w-4" />
                {isUploading ? 'Processing...' : 'Upload & Process'}
              </Button>
            </div>
          )}

          {/* PDF upload section */}
          {method === "pdf" && !pdfReviewMode && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload a PDF containing product information (title, ingredients, nutrition facts, images).
                AI will extract and structure the data for your review before uploading.
              </p>
              <div className="space-y-2">
                <Label htmlFor="pdf-file">Select PDF File</Label>
                <Input id="pdf-file" type="file" accept=".pdf,application/pdf" ref={fileInputRef} onChange={handleFileChange} className="cursor-pointer" />
                {file && <p className="text-sm text-muted-foreground">Selected: <span className="font-medium">{file.name}</span></p>}
              </div>
              <Button onClick={handleDocumentParse} disabled={!file || pdfParsing} className="gap-2">
                {pdfParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                {pdfParsing ? 'Analyzing PDF...' : 'Parse & Preview'}
              </Button>
            </div>
          )}

          {/* Image upload section */}
          {method === "image" && !pdfReviewMode && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload images of product labels, nutrition facts, or packaging.
                AI will extract and structure the data for your review before uploading. You can upload multiple images.
              </p>
              <div className="space-y-2">
                <Label htmlFor="image-files">Select Image(s)</Label>
                <Input id="image-files" type="file" accept="image/*" multiple onChange={handleImageFilesChange} className="cursor-pointer" />
                {imageFiles.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{imageFiles.length} image(s) selected</p>
                    <div className="flex flex-wrap gap-2">
                      {imageFiles.map((f, i) => (
                        <div key={i} className="relative h-16 w-16 rounded border overflow-hidden">
                          <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={handleDocumentParse} disabled={imageFiles.length === 0 || pdfParsing} className="gap-2">
                {pdfParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                {pdfParsing ? 'Analyzing Images...' : 'Parse & Preview'}
              </Button>
            </div>
          )}

          {/* Review Mode (PDF & Image) */}
          {(method === "pdf" || method === "image") && pdfReviewMode && pdfProducts.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Review Extracted Products</h3>
                  <p className="text-sm text-muted-foreground">{pdfProducts.length} product(s) found. Review and confirm before uploading.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setPdfReviewMode(false); setPdfProducts([]); }}>
                    Cancel
                  </Button>
                  <Button onClick={handlePDFConfirmUpload} disabled={isUploading} className="gap-2">
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {isUploading ? 'Uploading...' : 'Confirm & Upload'}
                  </Button>
                </div>
              </div>


              <Accordion type="multiple" defaultValue={pdfProducts.map((_, i) => `product-${i}`)}>
                {pdfProducts.map((product, idx) => (
                  <AccordionItem key={idx} value={`product-${idx}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{product.title || `Product ${idx + 1}`}</span>
                        <Badge variant="secondary">{product.nutrients.length} nutrients</Badge>
                        {product.nutrients.some((n) => !n.matched) && (
                          <Badge variant="destructive" className="text-xs">Unmatched nutrients</Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2">
                        {/* Extracted image preview */}
                        {product.extracted_image_url && (
                          <div className="flex items-center gap-3">
                            <img src={product.extracted_image_url} alt={product.title} className="h-20 w-20 object-cover rounded border" />
                            <span className="text-sm text-muted-foreground">Extracted product image</span>
                          </div>
                        )}
                        {/* Product details grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Brand Owner:</span> <span className="font-medium">{product.brand_owner || '—'}</span></div>
                          <div><span className="text-muted-foreground">Brand Name:</span> <span className="font-medium">{product.brand_name || '—'}</span></div>
                          <div><span className="text-muted-foreground">Serving Size:</span> <span className="font-medium">{product.serving_size} {product.serving_size_unit}</span></div>
                          <div><span className="text-muted-foreground">Household Serving:</span> <span className="font-medium">{product.household_serving_fulltext || '—'}</span></div>
                          <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{product.branded_food_category || '—'}</span></div>
                          <div><span className="text-muted-foreground">Package Weight:</span> <span className="font-medium">{product.package_weight || '—'}</span></div>
                        </div>

                        {product.description && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Description:</span>
                            <p className="mt-1">{product.description}</p>
                          </div>
                        )}

                        {product.ingredients && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Ingredients:</span>
                            <p className="mt-1 italic">{product.ingredients}</p>
                          </div>
                        )}

                        {/* Nutrients table */}
                        <div>
                          <h4 className="text-sm font-semibold mb-2">Nutrients</h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nutrient</TableHead>
                                <TableHead className="w-24">Amount</TableHead>
                                <TableHead className="w-20">Unit</TableHead>
                                <TableHead className="w-24">USDA ID</TableHead>
                                <TableHead className="w-20">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {product.nutrients.map((n, nIdx) => (
                                <TableRow key={nIdx} className={!n.matched ? 'bg-destructive/5' : ''}>
                                  <TableCell>{n.nutrient_name}</TableCell>
                                  <TableCell className="font-mono">{n.amount}</TableCell>
                                  <TableCell>{n.unit}</TableCell>
                                  <TableCell className="font-mono text-xs">{n.nutrient_id > 0 ? n.nutrient_id : '—'}</TableCell>
                                  <TableCell>
                                    {n.matched ? (
                                      <Badge variant="secondary" className="text-xs">Matched</Badge>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">Unmatched</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {product.nutrients.some((n) => !n.matched) && (
                            <p className="text-xs text-muted-foreground mt-2">
                              ⚠ Unmatched nutrients will be excluded from upload. Only matched nutrients with valid USDA IDs are saved.
                            </p>
                          )}
                        </div>

                        <div className="flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleRemovePdfProduct(idx)} className="text-destructive gap-1">
                            <Trash2 className="h-3 w-3" /> Remove product
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Validation errors */}
          {errors.length > 0 && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 space-y-2">
              <div className="flex items-center gap-2 text-destructive font-medium text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {errors.length} validation error{errors.length !== 1 ? 's' : ''} found
              </div>
              <ul className="text-sm text-destructive space-y-1 list-disc list-inside">
                {errors.map((e, i) => (
                  <li key={i}>
                    <span className="font-medium">Group {e.group}</span> &mdash; {e.field}: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results table */}
      {uploadComplete && results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Results</CardTitle>
            <CardDescription>
              {results.length} product{results.length !== 1 ? 's' : ''} added to staging.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">FDC ID</TableHead>
                  <TableHead className="w-20">Image</TableHead>
                  <TableHead>Title</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={r.fdc_id}>
                    <TableCell className="font-mono text-sm">{r.fdc_id}</TableCell>
                    <TableCell>
                      {r.image ? (
                        <img src={r.image} alt={r.title} className="h-10 w-10 object-cover rounded"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="h-10 w-10 bg-muted rounded flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{r.title}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="view" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Previous Uploads</CardTitle>
                  <CardDescription>All products uploaded to the staging catalog.</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => handleExportTable('branded_food')} disabled={!!exportingTable} className="gap-2">
                    {exportingTable === 'branded_food' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Products
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportTable('food_images')} disabled={!!exportingTable} className="gap-2">
                    {exportingTable === 'food_images' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Images
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportTable('food_nutrients')} disabled={!!exportingTable} className="gap-2">
                    {exportingTable === 'food_nutrients' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Nutrients
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchPreviousUploads} disabled={loadingUploads} className="gap-2">
                    <RefreshCw className={`h-4 w-4 ${loadingUploads ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loadingUploads ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : previousUploads.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No products uploaded yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">FDC ID</TableHead>
                      <TableHead className="w-20">Image</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Serving</TableHead>
                      <TableHead className="w-40">Uploaded</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previousUploads.map((p) => (
                      <TableRow key={p.fdc_id}>
                        <TableCell className="font-mono text-sm">{p.fdc_id}</TableCell>
                        <TableCell>
                          {p.image ? (
                            <img src={p.image} alt={p.title} className="h-10 w-10 object-cover rounded"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          ) : (
                            <div className="h-10 w-10 bg-muted rounded flex items-center justify-center">
                              <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium">{p.title}</span>
                            {p.description && <p className="text-xs text-muted-foreground truncate max-w-xs">{p.description}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{p.serving_size}{p.serving_size_unit}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(p.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
